import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CommonModal from '../../components/common/CommonModal';
import AttendanceFormModal from '../../components/Modals/AttendanceFormModal';
import AttendanceSlideModal from '../../components/Modals/AttendanceSlideModal';
import { MonthType, useAttendance, AttendanceResponseDto } from '../../hooks/useAttendance';
import { useStudents, Student } from '../../hooks/useStudents';
import { useSessionClass } from 'contexts/SessionClassContext';
import EnhancedTable from '../common/EnhancedTable';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'contexts/AuthContext';
import { useTenantHeader } from 'hooks/useTenantHeader';
import ActionButtons from '../common/ActionButtons';
import { useTeacher } from 'hooks/useTeacher';
import { format, parseISO, isValid } from 'date-fns';

const AttendanceManagement: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editDate, setEditDate] = useState<string | null>(null);
  const [editTeacherId, setEditTeacherId] = useState<number | null>(null);
  const [oneByOneSetupOpen, setOneByOneSetupOpen] = useState(false);
  const [slideModalOpen, setSlideModalOpen] = useState(false);
  const [oneByOneTeacherId, setOneByOneTeacherId] = useState<number | ''>('');
  const [oneByOneMonth, setOneByOneMonth] = useState<MonthType>('MAY');
  const [oneByOneYear, setOneByOneYear] = useState<number>(new Date().getFullYear());
  const [oneByOneDay, setOneByOneDay] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { selectedSession, selectedClass } = useSessionClass();

  const now = new Date();
  const [month, setMonth] = useState<MonthType>((() => {
    const m = now.getMonth();
    const months: MonthType[] = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER',
    ];
    return months[m] || 'JANUARY';
  })());
  const [year, setYear] = useState<number>(now.getFullYear());

  const { getAttendanceQuery, attendanceStatusQuery } = useAttendance({
    sessionId: selectedSession?.id || 0,
    classId: selectedClass?.id || 0,
    month,
    year,
  });

  const { user, isTeacher } = useAuth();
  const { tenantLoading } = useTenantHeader();
  const { studentsQuery } = useStudents(tenantLoading);
  const { teachersQuery } = useTeacher(user?.schoolId || 0, tenantLoading);

  const handleTakeAttendance = () => {
    setEditDate(null);
    setEditTeacherId(null);
    setModalOpen(true);
  };

  const isFutureDateSelected = useMemo(() => {
    if (!oneByOneYear || !oneByOneMonth || !oneByOneDay) return false;
    const months: MonthType[] = [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
    ];
    const mIdx = months.indexOf(oneByOneMonth);
    if (mIdx < 0) return false;
    const selectedDate = new Date(oneByOneYear, mIdx, oneByOneDay);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate > today;
  }, [oneByOneYear, oneByOneMonth, oneByOneDay]);

  const handleOpenOneByOneSetup = () => {
    setOneByOneSetupOpen(true);
    // default teacher: current user mapped teacher
    const currentTeacher = teachersQuery.data?.find(tt => tt.userId === user?.id);
    setOneByOneTeacherId(currentTeacher?.id ?? '');
    setOneByOneMonth(month === 'NA' ? 'MAY' : month);
    setOneByOneYear(year);
    setOneByOneDay(1);
  };

  const handleCloseModal = () => {
    setEditDate(null);
    setEditTeacherId(null);
    setModalOpen(false);
  };

  const handleCloseOneByOneSetup = () => {
    setOneByOneSetupOpen(false);
  };

  const handleNextOneByOne = () => {
    if (!oneByOneTeacherId || !oneByOneDay) return;
    setOneByOneSetupOpen(false);
    setSlideModalOpen(true);
  };

  const handleCloseSlideModal = () => {
    setSlideModalOpen(false);
  };

  const handleEditAttendance = (date: string, staffId?: number | null) => {
    setEditDate(date);
    setEditTeacherId(staffId ?? null);
    setModalOpen(true);
  };

  // Delete functionality is removed as it's not supported by the new API
  const handleCloseDeleteDialog = () => { }; // Placeholder
  const handleDelete = async () => { }; // Placeholder

  const getStudentName = (studentId: number) => {
    const student = studentsQuery.data?.find((s: Student) => s.id === studentId);
    return student ? `${student.studentName}` : '';
  };

  const columns = [
    { id: 'displayDate', label: t('date') },
    { id: 'className', label: t('class') },
    { id: 'teacherName', label: t('teacher') },
    { id: 'statusSummary', label: t('status') },
  ];

  const renderActions = (row: any) => (
    <ActionButtons
      onEdit={() => handleEditAttendance(row.date, row.staffId)}
    />
  );

  const filteredAttendance = getAttendanceQuery.data || [];

  const safeFormatIso = useCallback((value: string, fmt: string) => {
    try {
      const d = parseISO(value);
      if (!isValid(d)) return value || t('n/a');
      return format(d, fmt);
    } catch {
      return value || t('n/a');
    }
  }, [t]);

  const groupedData = useMemo(() => {
    const statusIdMap = new Map<number, string>();
    (attendanceStatusQuery.data || []).forEach((status) => {
      statusIdMap.set(status.id, String(status.statusCode).toUpperCase());
    });

    const pad2 = (n: number) => String(n).padStart(2, '0');
    const months: MonthType[] = [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
    ];

    const groups: Record<string, {
      date: string,
      presents: number,
      absents: number,
      leaves: number,
      halfdays: number,
      total: number,
      classId: number,
      staffId: number | null,
      lastUpdatedTime: string | null
    }> = {};

    filteredAttendance.forEach((record) => {
      const monthNum = months.indexOf(record.month as MonthType) + 1;
      if (monthNum <= 0 || !record.year) return;

      const dayStatusMap = record.dayStatusMap || {};
      Object.entries(dayStatusMap).forEach(([dayStr, statusId]) => {
        const day = Number(dayStr);
        if (isNaN(day) || day < 1 || day > 31) return;

        const code = statusId != null ? statusIdMap.get(Number(statusId)) : null;
        // Only count days where attendance has actually been marked
        if (code && code !== 'NOT-MARKED') {
          const dateStr = `${record.year}-${pad2(monthNum)}-${pad2(day)}`;
          if (!groups[dateStr]) {
            groups[dateStr] = {
              date: dateStr,
              presents: 0,
              absents: 0,
              leaves: 0,
              halfdays: 0,
              total: filteredAttendance.length,
              classId: record.classId,
              staffId: record.staffId || record.teacherId || null,
              lastUpdatedTime: record.lastUpdatedTime || null
            };
          }

          if (code === 'PRESENT') {
            groups[dateStr].presents++;
          } else if (code === 'ABSENT') {
            groups[dateStr].absents++;
          } else if (code === 'LEAVE') {
            groups[dateStr].leaves++;
          } else if (code === 'HALF_DAY') {
            groups[dateStr].halfdays++;
          }

          // Track the latest lastUpdatedTime and staffId
          if (record.lastUpdatedTime) {
            const currentGroup = groups[dateStr];
            if (!currentGroup.lastUpdatedTime || record.lastUpdatedTime > currentGroup.lastUpdatedTime) {
              currentGroup.lastUpdatedTime = record.lastUpdatedTime;
            }
          }
          if (record.staffId || record.teacherId) {
            groups[dateStr].staffId = record.staffId || record.teacherId || null;
          }
        }
      });
    });

    return Object.values(groups).map(group => {
      const teacher = teachersQuery.data?.find(t => t.id === group.staffId);
      const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : t('n/a');

      return {
        ...group,
        displayDate: safeFormatIso(group.date, month === 'NA' ? "MMM yyyy" : "dd-MM-yyyy"),
        className: selectedClass?.name || String(group.classId),
        teacherName,
        statusSummary: `${t('total')}: ${group.total}, ${t('present')}: ${group.presents}, ${t('absent')}: ${group.absents}, ${t('leave')}: ${group.leaves}, ${t('half_day', 'Half Day')}: ${group.halfdays}`,
        lastUpdated: group.lastUpdatedTime ? safeFormatIso(group.lastUpdatedTime, "dd-MM-yyyy HH:mm") : t('n/a'),
      };
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredAttendance, selectedClass, t, teachersQuery.data, attendanceStatusQuery.data, month, safeFormatIso]);



  return (
    <Paper sx={{ p: 1.5, maxWidth: 1500, mx: 'auto', mt: 1, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}
        >
          {t('attendance_management')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
            onClick={handleTakeAttendance}
          >
            {t('attendance_register')}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
            onClick={handleOpenOneByOneSetup}
          >
            {t('one_by_one_attendance')}
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 36 }  }}>
          <InputLabel>{t('month')}</InputLabel>
          <Select
            label={t('month')}
            value={month}
            onChange={(e) => setMonth(e.target.value as MonthType)}
          >
            <MenuItem value="NA">{t('all')}</MenuItem>
            <MenuItem value="JANUARY">{t('january')}</MenuItem>
            <MenuItem value="FEBRUARY">{t('february')}</MenuItem>
            <MenuItem value="MARCH">{t('march')}</MenuItem>
            <MenuItem value="APRIL">{t('april')}</MenuItem>
            <MenuItem value="MAY">{t('may')}</MenuItem>
            <MenuItem value="JUNE">{t('june')}</MenuItem>
            <MenuItem value="JULY">{t('july')}</MenuItem>
            <MenuItem value="AUGUST">{t('august')}</MenuItem>
            <MenuItem value="SEPTEMBER">{t('september')}</MenuItem>
            <MenuItem value="OCTOBER">{t('october')}</MenuItem>
            <MenuItem value="NOVEMBER">{t('november')}</MenuItem>
            <MenuItem value="DECEMBER">{t('december')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100, '& .MuiInputBase-root': { height: 36 }  }}>
          <InputLabel>{t('year')}</InputLabel>
          <Select
            label={t('year')}
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
              <MenuItem key={y} value={String(y)}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder={t('search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
        />
      </Box>

      <EnhancedTable
        columns={columns}
        data={groupedData}
        searchFields={['displayDate', 'className']}
        hideSearch
        externalSearchTerm={searchTerm}
        filterFields={[]}
        actions={renderActions}
        maxHeight="none"
        cardProps={{
          elevation: 0,
          sx: {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            mb: 0,
            '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } }
          }
        }}
      />

      <AttendanceFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        initialDate={editDate}
        initialTeacherId={editTeacherId}
      />

      <CommonModal
        open={oneByOneSetupOpen}
        onClose={handleCloseOneByOneSetup}
        title={t('one_by_one_attendance')}
        maxWidth="sm"
        onSubmit={handleNextOneByOne}
        submitLabel={t('next')}
        cancelLabel={t('cancel')}
        submitDisabled={!oneByOneTeacherId || !oneByOneDay || isFutureDateSelected}
        contentSx={{ pt: 2 }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {isFutureDateSelected && (
            <Typography variant="body2" color="error" sx={{ width: '100%', mb: 1 }}>
              Cannot mark attendance for a future date.
            </Typography>
          )}
          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel>{t('teacher_name')}</InputLabel>
            <Select
              label={t('teacher_name')}
              value={oneByOneTeacherId === '' ? '' : String(oneByOneTeacherId)}
              onChange={(e) => setOneByOneTeacherId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={isTeacher()}
            >
              {!isTeacher() && <MenuItem value="">{t('select')}</MenuItem>}
              {teachersQuery.data
                ?.filter((teacher) => !isTeacher() || teacher.userId === user?.id)
                ?.map((teacher) => (
                  <MenuItem key={teacher.id} value={String(teacher.id)}>
                    {teacher.firstName} {teacher.lastName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('month')}</InputLabel>
            <Select
              label={t('month')}
              value={oneByOneMonth}
              onChange={(e) => setOneByOneMonth(e.target.value as MonthType)}
            >
              <MenuItem value="JANUARY">{t('january')}</MenuItem>
              <MenuItem value="FEBRUARY">{t('february')}</MenuItem>
              <MenuItem value="MARCH">{t('march')}</MenuItem>
              <MenuItem value="APRIL">{t('april')}</MenuItem>
              <MenuItem value="MAY">{t('may')}</MenuItem>
              <MenuItem value="JUNE">{t('june')}</MenuItem>
              <MenuItem value="JULY">{t('july')}</MenuItem>
              <MenuItem value="AUGUST">{t('august')}</MenuItem>
              <MenuItem value="SEPTEMBER">{t('september')}</MenuItem>
              <MenuItem value="OCTOBER">{t('october')}</MenuItem>
              <MenuItem value="NOVEMBER">{t('november')}</MenuItem>
              <MenuItem value="DECEMBER">{t('december')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('year')}</InputLabel>
            <Select
              label={t('year')}
              value={String(oneByOneYear)}
              onChange={(e) => setOneByOneYear(Number(e.target.value))}
            >
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('day')}</InputLabel>
            <Select
              label={t('day')}
              value={String(oneByOneDay)}
              onChange={(e) => setOneByOneDay(Number(e.target.value))}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={String(d)}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </CommonModal>

      <AttendanceSlideModal
        {...({
          open: slideModalOpen,
          onClose: handleCloseSlideModal,
          presetTeacherId: oneByOneTeacherId === '' ? undefined : Number(oneByOneTeacherId),
          presetMonth: oneByOneMonth,
          presetYear: oneByOneYear,
          presetDay: oneByOneDay || undefined,
        } as any)}
      />

    </Paper>
  );
};

export default AttendanceManagement;